<script lang="ts" setup>
import { format, parse, parseISO, isAfter } from 'date-fns'
import {
  ToccoCourseFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoModuleFieldsFragment,
  ToccoWebinarFieldsFragment,
} from '~/api/graphql/generated'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  entity:
    | ToccoWebinarFieldsFragment
    | ToccoCourseFieldsFragment
    | ToccoModuleFieldsFragment
    | ToccoIntroModuleFieldsFragment
    | ToccoELearningFieldsFragment
  showNextDatesLink?: boolean
}>()

const { t } = useI18n()

const computedEntity = computed(() => props.entity)
const fieldToccoDataJSON = computed(() => JSON.parse(computedEntity.value.fieldToccoData ?? ''))

const formattedStartDate = computed(() =>
  computedEntity.value.fieldStartDate?.value
    ? format(parse(computedEntity.value.fieldStartDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)
const formattedEndDate = computed(() =>
  computedEntity.value.fieldEndDate?.value
    ? format(parse(computedEntity.value.fieldEndDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)

// TODO: add types
const nextDatesList = computed(() =>
  fieldToccoDataJSON.value.paths.relReservation.value
    ? fieldToccoDataJSON.value.paths.relReservation.value
        .map((reservation: { paths: { date_from: { value: string } } }) => parseISO(reservation.paths.date_from.value))
        .sort((firstDate: Date, secondDate: Date) => (isAfter(firstDate, secondDate) ? 1 : -1))
        .map((nextDate: Date) => format(nextDate, 'dd.MM.yyyy'))
        .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index)
        .join(', ')
    : '',
)
</script>

<template>
  <Section>
    <div class="t-10 t-o-1 tl-7 tl-o-0 ds-o-1">
      <h2>{{ t('tocco.detail.dates') }}</h2>
      <div v-animate="{ animation: 'vertical-reveal' }" class="dates">
        <div class="date">
          <h3 class="text-small">{{ t('tocco.detail.startDateLabel') }}</h3>
          <p>{{ formattedStartDate }}</p>
        </div>
        <div class="date">
          <h3 class="text-small">{{ t('tocco.detail.endDateLabel') }}</h3>
          <p>{{ formattedEndDate }}</p>
        </div>

        <TextLink v-if="showNextDatesLink" v-animate="{ animation: 'vertical-reveal' }" to="#next-modules">
          {{ t('tocco.detail.nextDatesLinkLabel') }}
        </TextLink>

        <div v-if="nextDatesList" v-animate="{ animation: 'vertical-reveal' }" class="date next-dates">
          <h3 class="text-small">{{ t('tocco.detail.classDates') }}</h3>
          <p>{{ nextDatesList }}</p>
        </div>
      </div>
    </div>
  </Section>
</template>

<style lang="scss" scoped>
.dates {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @include breakpoint(tl) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  .date {
    padding-bottom: 12px;
    border-bottom: 1px solid var(--c-line-light);

    p {
      margin: 0;
    }

    &.next-dates {
      @include breakpoint(tl) {
        grid-column: span 2;
      }
    }
  }
}
</style>
